import React, { useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { generateAttendanceCode } from '../../components/GenerateAttendanceCode';
import useFetch from '../../hooks/useFetch';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faLocationDot, faSave, faArrowLeft, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';

const ChronoCheckHome = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData, { loading }] = useFetch();

  const navigate = useNavigate();

  const [userId] = useState(authState.user?._id);
  const [userCode] = useState(authState.user?.think5whyscode);
  const [userName] = useState(authState.user?.name);

  const [qrData, setQrData] = useState(null);
  const [geoLocation, setGeoLocation] = useState({ latitude: null, longitude: null });
  const [attendanceRemarks, setAttendanceRemarks] = useState("");
  const [attendanceFor, setAttendanceFor] = useState("");
  const [locationName, setLocationName] = useState("");
  const [attendanceCode] = useState(generateAttendanceCode());
  const [withoutQR, setWithoutQR] = useState(false);
  const [withoutGeoLocation, setWithoutGeoLocation] = useState(false);

  const [parsedData, setParsedData] = useState({
    ecode: '', scode: '', qloc: '', qlat: 0, qlong: 0
  });

  const [attendanceSaved, setAttendanceSaved] = useState(false);
  const [error, setError] = useState("");
  const [marking, setMarking] = useState(false);

  // Geolocation
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => setGeoLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
        (err) => console.warn("Geolocation error:", err.message)
      );
    }
  }, []);

  // QR Scanner
  useEffect(() => {
    const scanner = new Html5QrcodeScanner("qr-reader", {
      fps: 10,
      qrbox: { width: 260, height: 260 }
    });

    scanner.render(
      (result) => setQrData(result),
      (error) => console.warn(`QR Error: ${error}`)
    );

    return () => scanner.clear();
  }, []);

  // Parse QR
  useEffect(() => {
    if (qrData) {
      const regex = /\[([^\]]+)\]/g;
      const matches = [...qrData.matchAll(regex)];
      if (matches.length >= 5) {
        setParsedData({
          ecode: matches[0][1],
          scode: matches[1][1],
          qloc: matches[2][1],
          qlat: matches[3][1],
          qlong: matches[4][1],
        });
      }
    }
  }, [qrData]);

  const markAttendance = async () => {
    if (!attendanceFor) {
      setError("Please select purpose of attendance");
      return;
    }
    setError("");
    setMarking(true);

    const attendanceRecord = {
      chronoType: 1,
      attendanceCode,
      employerCode: parsedData.ecode,
      supervisorCode: parsedData.scode,
      userId,
      userCode,
      userName,
      inTime: new Date().toISOString(),
      outTime: null,
      locationName: locationName || parsedData.qloc,
      empCode: userCode,
      empName: userName,
      qrLat: parsedData.qlat,
      qrLong: parsedData.qlong,
      locationLat: geoLocation.latitude,
      locationLong: geoLocation.longitude,
      placeCode: parsedData.qloc,
      placeName: locationName,
      isOffline: !navigator.onLine,
      attendanceFor,
      remarks: attendanceRemarks,
      createdBy: userId,
      attendanceDate: new Date(),
      startTime: new Date(),
      endTime: null,
      dateCreated: new Date(),
      isActive: true,
      isDeleted: false,
      isVerified: false,
      verifierCode: '',
      verifierName: '',
      verifierRemarks: '',
      verifiedDate: null,
      isApproved: false,
      approverCode: '',
      approverName: '',
      approverRemarks: '',
      approvedDate: null,
      wagesRate1: 0.00,
      wagesRate2: 0.00,
      expense: 0.00,
      expRemarks: "",
      expApproved: false,
      expApprovalRemarks: "",
      expApprovedBy: "",
      expApprovalDate: null,
      isTask: false,
      refAttendanceCode: '',
      isImportant: false,
      isUrgent: false,
      oEstimate: 0.00,
      pEstimate: 0.00,
      mlEstimate: 0.00,
      evalEstimate: 0.00,
      estimateUnit: 'hr',
      oRemarks: '',
      pRemarks: '',
      mlRemarks: '',
      projectCode: '',
      cost: 0.00,
      costUnit: "₹",
      bac: 0.00,
      ppcomplete: 0.00,
      pv: 0.00,
      apcomplete: 0.00,
      ev: 0.00,
      ac: 0.00,
      cv: 0.00,
      sv: 0.00,
      cpi: 0.00,
      cpic: 0.00,
      spi: 0.00,
      eac: 0.00,
      etc: 0.00,
      vac: 0.00,
      withoutQR,
      withoutGeoLocation,
    };

    try {
      const config = {
        url: "/attendance",
        method: "post",
        data: { attendanceRecord, user: userId },
        headers: { Authorization: authState.token }
      };

      await fetchData(config);
      setAttendanceSaved(true);
    } catch (err) {
      console.error(err);
      setError("Failed to save attendance. Please try again.");
    } finally {
      setMarking(false);
    }
  };

  const navigateBack = () => navigate(-1);
  const goToMyAttendance = () => navigate("/attendance-employee");
  const goToTeamAttendance = () => navigate("/attendance-supervisor");

  return (
    <div className="min-h-screen bg-zinc-950 text-gray-100 pb-12">
      <header className="sticky top-0 z-50 bg-zinc-900 border-b border-yellow-500/30 px-4 py-2">
        <h1 className="text-lg lg:text-2xl font-bold text-yellow-200 text-center">Think5Whys ChronoCheck - Attendance</h1>
      </header>

      <div className="max-w-2xl mx-auto px-4 pt-2">
        {/* Status */}
        <div className="flex justify-between items-center mb-6 text-sm">
          <div className={`flex items-center gap-2 ${navigator.onLine ? 'text-emerald-400' : 'text-orange-400'}`}>
            <div className={`w-3 h-3 rounded-full ${navigator.onLine ? 'bg-emerald-400' : 'bg-orange-400 animate-pulse'}`} />
            {navigator.onLine ? 'Online' : 'Offline Mode'}
          </div>
          <div className="text-gray-400">{new Date().toLocaleString()}</div>
        </div>

        {/* QR Scanner */}
        <div className="bg-zinc-900 rounded-3xl p-6 border border-zinc-700 mb-2">
          <div className="flex items-center gap-3 mb-4">
            <FontAwesomeIcon icon={faQrcode} className="text-2xl text-yellow-400" />
            <h3 className="text-lg font-semibold text-cyan-200">Scan QR Code</h3>
          </div>

          <label className="flex items-center gap-2 mb-5 cursor-pointer">
            <input
              type="checkbox"
              checked={withoutQR}
              onChange={(e) => setWithoutQR(e.target.checked)}
              className="accent-yellow-400 w-5 h-5"
            />
            <span>Proceed without QR Code</span>
          </label>

          <div id="qr-reader" className="w-full bg-black rounded-2xl min-h-[280px]" />

          {(qrData || withoutQR) && (
            <div className="mt-2 p-2 bg-zinc-800 text-gray-100 rounded-2xl text-sm space-y-2">
              {qrData && (
                <>
                  <p className="text-gray-100"><span className="text-gray-400">Employer:</span> {parsedData.ecode}</p>
                  <p className="text-gray-100"><span className="text-gray-400">Supervisor:</span> {parsedData.scode}</p>
                  <p className="text-gray-100"><span className="text-gray-400">QR Location:</span> {parsedData.qloc}</p>
                </>
              )}
              <p className="text-gray-100"><span className="text-gray-400">Current Location:</span> {geoLocation.latitude?.toFixed(5)} / {geoLocation.longitude?.toFixed(5)}</p>
              <p className="text-gray-100"><span className="text-gray-400">Employee:</span> {userName} ({userCode})</p>
            </div>
          )}
        </div>

        {/* Form */}
        {(qrData || withoutQR) && (
          <div className="bg-zinc-900 rounded-3xl p-2 border border-zinc-700 mb-4">
            <h3 className="font-semibold mb-5 flex items-center gap-2 text-cyan-200">
              <FontAwesomeIcon icon={faLocationDot} className="text-cyan-400" /> Attendance Details
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-gray-400 mb-2">Purpose of Record</label>
                <select
                  value={attendanceFor}
                  onChange={(e) => setAttendanceFor(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-2 py-1 focus:border-yellow-400"
                >
                  <option value="">--- Select Purpose ---</option>
                  <option value="Attendance">Attendance</option>
                  <option value="Break">Break</option>
                  <option value="Injury">Injury</option>
                  <option value="Instructed Attendance">Instructed Attendance</option>
                  <option value="Leave">Leave</option>
                  <option value="Machine">Machine</option>
                  <option value="Medical">Medical</option>
                  <option value="Overtime">Overtime</option>
                  <option value="Rest Day">Rest Day</option>
                  <option value="Self Attendance">Self Attendance</option>
                  <option value="Task">Task</option>
                  <option value="Tour">Tour</option>
                  <option value="Training">Training</option>
                  <option value="Visit">Visit</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-400 mb-2">Remarks</label>
                <textarea
                  value={attendanceRemarks}
                  onChange={(e) => setAttendanceRemarks(e.target.value)}
                  placeholder="Enter remarks here..."
                  style={{height: "100px"}}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-2 py-1 focus:border-yellow-400 resize-y"
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-2">Location Name (Optional)</label>
                <input
                  type="text"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="e.g. Factory Floor A"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-2 py-1 focus:border-yellow-400"
                />
              </div>
            </div>
          </div>
        )}

        {error && <p className="text-red-400 text-center mb-4 font-medium">{error}</p>}
        {attendanceSaved && <p className="text-emerald-400 text-center mb-2 font-medium">✅ Attendance Saved Successfully!</p>}

        <div className="flex flex-row justify-center gap-2 px-1">
          <button
            onClick={markAttendance}
            disabled={marking || attendanceSaved || !attendanceFor}
            className="w-fit px-2 py-1 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 rounded-2xl font-semibold flex items-center justify-center gap-3 text-lg transition"
          >
            <FontAwesomeIcon icon={faSave} />
            {marking ? "Saving..." : "Save Attendance Record"}
          </button>

          <button
            onClick={navigateBack}
            className="w-fit px-2 py-1 border border-zinc-700 hover:bg-zinc-800 rounded-2xl flex items-center justify-center gap-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
        </div>

        <div className="flex flex-row justify-center gap-2 mt-2">
          <button
            onClick={goToMyAttendance}
            className="bg-zinc-900 border border-zinc-700 hover:border-yellow-400 w-fit p-4 rounded-xl flex flex-col items-center gap-4 transition"
          >
            <FontAwesomeIcon icon={faUser} className="text-4xl text-yellow-400" />
            <span className="font-medium">My Attendance</span>
          </button>

          <button
            onClick={goToTeamAttendance}
            className="bg-zinc-900 border border-zinc-700 hover:border-purple-400 w-fit p-4 rounded-xl flex flex-col items-center gap-4 transition"
          >
            <FontAwesomeIcon icon={faUsers} className="text-4xl text-purple-400" />
            <span className="font-medium">Team Attendance</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChronoCheckHome;